import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  SquaresFour, Student, UsersThree, Chalkboard, CalendarCheck, Exam, Clock, CurrencyInr, Notebook, Megaphone,
  ChartBar, Gear, Trophy, Bell, Buildings, SignOut, List, Key,
} from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "./ui/dropdown-menu";
import { Sheet, SheetContent } from "./ui/sheet";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { api, errMsg } from "../lib/api";
import { Modal, Field } from "./kit";
import { Coin3D, WelcomeIntro, useWelcome } from "./Welcome";
import { SchoolMark } from "./Photo";

const NAV = {
  superadmin: [["Schools", "/platform", Buildings]],
  admin: [
    ["Dashboard", "/dashboard", SquaresFour], ["Students", "/students", Student], ["Staff", "/staff", UsersThree],
    ["Classes", "/classes", Chalkboard], ["Attendance", "/attendance", CalendarCheck], ["Exams & Marks", "/exams", Exam],
    ["Timetable", "/timetable", Clock], ["Fees", "/fees", CurrencyInr], ["Homework", "/homework", Notebook],
    ["Notices", "/notices", Megaphone], ["Reports", "/reports", ChartBar], ["Settings", "/settings", Gear],
  ],
  teacher: [
    ["Dashboard", "/dashboard", SquaresFour], ["Attendance", "/attendance", CalendarCheck], ["Homework", "/homework", Notebook],
    ["Exams & Marks", "/exams", Exam], ["Reports", "/reports", ChartBar], ["Students", "/students", Student],
    ["Timetable", "/timetable", Clock], ["Notices", "/notices", Megaphone],
  ],
  parent: [
    ["Dashboard", "/dashboard", SquaresFour], ["Attendance", "/attendance", CalendarCheck], ["Fees", "/fees", CurrencyInr],
    ["Homework", "/homework", Notebook], ["Results", "/results", Trophy], ["Timetable", "/timetable", Clock],
    ["Notices", "/notices", Megaphone], ["Updates", "/updates", Bell],
  ],
  student: [
    ["Dashboard", "/dashboard", SquaresFour], ["Timetable", "/timetable", Clock], ["Homework", "/homework", Notebook],
    ["Results", "/results", Trophy], ["Notices", "/notices", Megaphone], ["Updates", "/updates", Bell],
  ],
};

function NavItems({ role, onNavigate }) {
  const { t } = useT();
  return (
    <nav className="flex flex-col gap-1">
      {(NAV[role] || []).map(([label, to, Icon]) => (
        <NavLink key={to} to={to} onClick={onNavigate} data-testid={`nav-${to.slice(1)}`}
          className={({ isActive }) =>
            `group relative flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
              isActive ? "bg-white/[0.07] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.04]"}`}>
          {({ isActive }) => (
            <>
              {isActive && <motion.span layoutId="nav-pill" className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-gradient-to-b from-blue-400 to-orange-400" />}
              <Icon size={19} weight={isActive ? "fill" : "duotone"} className={isActive ? "text-blue-300" : ""} />
              <span>{t(label)}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

const Brand = ({ school }) => (
  <div className="flex items-center gap-3 px-2">
    <Coin3D size="sm" testid="sidebar-logo" />
    <div className="min-w-0">
      <div className="font-display text-lg font-semibold tracking-tight text-white leading-none">Schola<span className="text-orange-400">-AI</span></div>
      <div className="mt-1.5 flex items-center gap-1.5 max-w-[160px]">
        <SchoolMark school={school} className="h-5 w-5 shrink-0 rounded-md bg-white p-0.5" testid="sidebar-school-logo" />
        <span className="truncate text-[11px] text-zinc-500" data-testid="sidebar-school-name">{school?.name || "Platform console"}</span>
      </div>
    </div>
  </div>
);

function LangToggle() {
  const { lang, setLang } = useT();
  return (
    <div className="flex rounded-full bg-white/5 border border-white/10 p-1" data-testid="language-toggle">
      {[["en", "EN"], ["hi", "हिं"]].map(([k, l]) => (
        <button key={k} onClick={() => { setLang(k); api.put("/auth/language", { language: k }).catch(() => {}); }}
          data-testid={`lang-${k}-btn`}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors duration-200 ${lang === k ? "bg-blue-500 text-white" : "text-zinc-400 hover:text-white"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

function PasswordDialog({ open, setOpen }) {
  const { t } = useT();
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", f);
      toast.success("Password updated");
      setOpen(false);
      setF({ current_password: "", new_password: "" });
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  return (
    <Modal open={open} onOpenChange={setOpen} title={t("Change password")} testid="change-password-dialog">
      <form onSubmit={save} className="space-y-4">
        <Field label="Current password"><input type="password" className="field" required value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="current-password-input" /></Field>
        <Field label="New password"><input type="password" className="field" required minLength={6} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="new-password-input" /></Field>
        <button className="btn-primary w-full" data-testid="change-password-submit">{t("Save")}</button>
      </form>
    </Modal>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [pw, setPw] = useState(false);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-3 rounded-full bg-white/5 border border-white/10 pl-1.5 pr-4 py-1.5 hover:bg-white/10 transition-colors duration-200" data-testid="user-menu-trigger">
            <span className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-blue-500/70 to-orange-500/70 text-xs font-bold text-white">
              {user.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}
            </span>
            <span className="hidden sm:block text-left leading-tight">
              <span className="block text-sm font-semibold text-white max-w-[140px] truncate">{user.name}</span>
              <span className="block text-[11px] capitalize text-zinc-400" data-testid="user-role">{user.role === "superadmin" ? t("Platform owner") : user.role}</span>
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 bg-[#0c0c16] border-white/10 text-zinc-200 rounded-2xl">
          <DropdownMenuLabel className="text-xs text-zinc-500 font-normal truncate">{user.email}</DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-white/10" />
          <DropdownMenuItem onClick={() => setPw(true)} data-testid="menu-change-password"><Key size={16} className="mr-2" /> {t("Change password")}</DropdownMenuItem>
          <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-btn" className="text-rose-300"><SignOut size={16} className="mr-2" /> {t("Sign out")}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <PasswordDialog open={pw} setOpen={setPw} />
    </>
  );
}

export default function Shell() {
  const { user } = useAuth();
  const loc = useLocation();
  const [mob, setMob] = useState(false);
  const role = user.role;
  const [welcome, skipWelcome, welcomeInfo] = useWelcome(user);
  return (
    <div className="ambient min-h-screen text-white">
      <WelcomeIntro show={welcome} onSkip={skipWelcome} user={user} info={welcomeInfo} />
      <div className="grain" />
      <aside className="no-print fixed inset-y-0 left-0 z-30 hidden w-[264px] flex-col gap-8 border-r border-white/5 bg-black/40 backdrop-blur-2xl px-4 py-6 lg:flex">
        <Brand school={user.school} />
        <div className="flex-1 overflow-y-auto pr-1"><NavItems role={role} /></div>
        <div className="rounded-2xl bg-gradient-to-br from-blue-500/10 to-orange-500/10 border border-white/10 p-4">
          <div className="eyebrow !text-[10px]">Academic year</div>
          <div className="mt-1 font-display text-white">2026–27</div>
        </div>
      </aside>
      <Sheet open={mob} onOpenChange={setMob}>
        <SheetContent side="left" className="w-[280px] bg-[#07070d] border-white/10 p-5 text-white">
          <div className="mb-8 mt-2"><Brand school={user.school} /></div>
          <NavItems role={role} onNavigate={() => setMob(false)} />
        </SheetContent>
      </Sheet>
      <div className="relative z-10 lg:pl-[264px]">
        <header className="no-print sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-white/5 bg-[#05050a]/70 backdrop-blur-2xl px-4 sm:px-8 py-3.5">
          <div className="flex items-center gap-3">
            <button className="btn-ghost !px-3 lg:hidden" onClick={() => setMob(true)} data-testid="mobile-menu-btn"><List size={20} /></button>
            <span className="hidden md:block text-sm text-zinc-400">{user.school?.name || "Schola-AI Platform"}</span>
          </div>
          <div className="flex items-center gap-3">
            <LangToggle />
            <UserMenu />
          </div>
        </header>
        <AnimatePresence mode="wait">
          <motion.main key={loc.pathname} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
            className="mx-auto max-w-[1440px] px-4 sm:px-8 py-8 sm:py-12">
            <Outlet />
          </motion.main>
        </AnimatePresence>
      </div>
    </div>
  );
}
